import React, { useCallback, useMemo } from 'react'
import { useQueryDepartments } from 'src/queries/organizations/department.queries'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import {
  SpendChartPoint,
  SpendChartView,
} from 'src/queries/reporting/reporting.models'
import { useQueryOwnUser } from 'src/queries/users.queries'

import {
  firstPresentMetadataValue,
  getPointDisplayLabel,
  isOpaqueReportingKey,
  PanelGatedState,
  ReportingPanelQueryParams,
} from './reportingPanelUtils'
import SpendStackedBarPanel from './SpendStackedBarPanel'

const getDepartmentId = (point: SpendChartPoint): string | undefined =>
  firstPresentMetadataValue(point, ['departmentId', 'teamId', 'id']) ??
  point.seriesKey ??
  point.key ??
  undefined

const chartNeedsDepartmentHydration = (
  chart: SpendChartView | undefined,
): boolean =>
  (chart?.points ?? []).some((point) => {
    const label = getPointDisplayLabel(point, '', [
      'departmentName',
      'teamName',
      'name',
    ])
    return isOpaqueReportingKey(label)
  })

const SpendByDepartmentPanel: React.FC<ReportingPanelQueryParams> = (props) => {
  const { data: currentUser } = useQueryOwnUser({})
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: props.organizationId,
  })

  const departmentsQuery = useQueryDepartments({
    organizationId: props.organizationId,
    includeArchived: true,
    enabled: props.enabled,
  })

  const gatedState: PanelGatedState | undefined =
    orgFeatureFlags && !orgFeatureFlags.departmentsEnabled
      ? { type: 'feature_disabled' }
      : departmentsQuery.isFetched && (departmentsQuery.data?.length ?? 0) === 0
        ? { type: 'not_configured' }
        : undefined

  const departmentsById = useMemo(
    () =>
      new Map(
        (departmentsQuery.data ?? []).map((department) => [
          department.id,
          department.name,
        ]),
      ),
    [departmentsQuery.data],
  )

  const seriesLabelForPoint = useCallback(
    (point: SpendChartPoint): string => {
      const hydratedLabel = departmentsById.get(getDepartmentId(point) ?? '')
      if (hydratedLabel !== undefined && !isOpaqueReportingKey(hydratedLabel)) {
        return hydratedLabel
      }

      return getPointDisplayLabel(point, 'Unknown team', [
        'departmentName',
        'teamName',
        'name',
      ])
    },
    [departmentsById],
  )

  return (
    <SpendStackedBarPanel
      {...props}
      chartId="spend_by_department"
      title="Spend by team"
      description="Spend grouped by team."
      summaryLabel="Top Team"
      seriesLabelForPoint={seriesLabelForPoint}
      shouldHoldForLabels={chartNeedsDepartmentHydration}
      isLabelHydrationLoading={departmentsQuery.isLoading}
      isLabelHydrationError={departmentsQuery.isError}
      onRetryLabels={departmentsQuery.refetch}
      gatedState={gatedState}
      isAdmin={currentUser?.hasOrgAdminPermissions}
      enabled={props.enabled && !gatedState}
    />
  )
}

export default SpendByDepartmentPanel
